/*
 * LinksPage — the reference "Share links" dashboard, wired to the real embeds API.
 * DOM/classes/copy kept 1:1; demo ROWS → real embeds.
 *
 * Summary tiles ← getLinksSummary(). Rows ← listEmbeds("") — the existing client
 * wrapper appends `?modelId=`, which the server treats as "no filter" and returns
 * every workspace embed (there is no dedicated no-arg list, and vaultApi may not be
 * edited). Model names are resolved best-effort via getModel. Access badge from
 * embed.access; status from expiresAt (Active / Expiring ≤7d / Expired). Copy uses
 * the real embed URL; Revoke/Delete → deleteEmbed; Renew → updateEmbed(+30d). The
 * unsupported Password badge is dropped (no backend field).
 */
import { useCallback, useEffect, useState } from "react";
import { confirmDialog } from "@/shared/ui";
import { useApp } from "../vault/ctx";
import { CopyBtn } from "../vault/Common";
import Ic from "../vault/Ic";
import {
  listEmbeds,
  getLinksSummary,
  deleteEmbed,
  updateEmbed,
  getModel,
  type VaultEmbed,
  type VaultModel,
  type LinksSummary,
} from "@/features/vault/vaultApi";

const ACCESS_BADGE: Record<string, [string, string]> = {
  view: ["gray", "View only"],
  download: ["green", "Can download"],
  comment: ["blue", "Can comment"],
  approve: ["gold", "Can approve"],
};

function statusOf(e: VaultEmbed): { badge: [string, string]; dim: boolean } {
  if (!e.expiresAt) return { badge: ["green", "Active"], dim: false };
  const t = +new Date(e.expiresAt), now = Date.now();
  if (t <= now) return { badge: ["gray", "Expired"], dim: true };
  if (t - now <= 7 * 86_400_000) return { badge: ["red", "Expiring"], dim: false };
  return { badge: ["green", "Active"], dim: false };
}
const fmtExp = (e: VaultEmbed) => (e.expiresAt ? new Date(e.expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const urlFor = (e: VaultEmbed) => `${window.location.origin}/embed/${e.token}`;

export default function LinksPage() {
  const { open, setClient, setModalData } = useApp();
  const [summary, setSummary] = useState<LinksSummary | null>(null);
  const [embeds, setEmbeds] = useState<VaultEmbed[] | null>(null);
  const [models, setModels] = useState<Record<string, VaultModel>>({});

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([
      getLinksSummary().catch(() => null),
      listEmbeds("").catch(() => [] as VaultEmbed[]),
    ]);
    setSummary(s);
    setEmbeds(e);
    const ids = [...new Set(e.map((x) => x.modelId))];
    const pairs = await Promise.all(ids.map(async (mid) => {
      try { return [mid, await getModel(mid)] as const; } catch { return [mid, null] as const; }
    }));
    setModels(Object.fromEntries(pairs.filter((p): p is [string, VaultModel] => p[1] !== null)));
  }, []);
  useEffect(() => { void load(); }, [load]);

  const revoke = async (e: VaultEmbed) => {
    if (!(await confirmDialog({ title: "Revoke this link?", body: "Anyone using it will lose access.", danger: true, confirmLabel: "Revoke" }))) return;
    deleteEmbed(e._id).then(() => void load());
  };
  const renew = (e: VaultEmbed) => {
    const expiresAt = new Date(Date.now() + 30 * 86_400_000).toISOString();
    updateEmbed(e._id, e.allowedDomains, e.options, { expiresAt }).then(() => void load());
  };
  // Open the shared Share modal, threading the link's model (or clearing stale
  // modal data when there's no model to target) — modalData is sticky otherwise.
  const openShare = (model: VaultModel | null) => {
    setModalData("share", model);
    setModalData("preview", model);
    open("share");
  };

  const tile = (v: number | undefined, label: string, red?: boolean) => (
    <div><div style={{ fontSize: 24, fontWeight: 800, ...(red ? { color: "var(--red)" } : {}) }}>{v ?? "—"}</div><div className="hint">{label}</div></div>
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div className="page-title">Share links</div>
          <div className="page-sub">Every live link, who it points at, and what it has been doing.</div>
        </div>
        <button className="btn ghost" onClick={() => setClient(true)}><Ic n="eye" size={14} /> Preview client view</button>
        <button className="btn primary" onClick={() => openShare(null)}>+ New share link</button>
      </div>
      <div className="two-col" style={{ margin: '16px 0 14px' }}>
        <div className="card" style={{ display: 'flex', gap: 26, alignItems: 'center', padding: '16px 22px' }}>
          {tile(summary?.activeLinks, "active links")}
          {tile(summary?.views30d, "views (30 d)")}
          {tile(summary?.downloads30d, "downloads (30 d)")}
          {tile(summary?.expiringThisWeek, "expiring this week", true)}
        </div>
        <div className="card" style={{ padding: '16px 22px' }}>
          <h3 style={{ fontSize: 13 }}>Link defaults</h3>
          <div className="hint" style={{ marginBottom: 4 }}>Applied to every new link — override per link when creating it.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <span className="badge gray">View only</span><span className="badge gray">Expires in 30 days</span><span className="badge gray">Watermark on</span><span className="badge gold">Your branding</span>
          </div>
        </div>
      </div>
      <table className="list-table on">
        <thead><tr><th style={{ width: '30%' }}>Link target</th><th>Access</th><th>Views</th><th>Downloads</th><th>Expires</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
        <tbody>
          {embeds === null ? (
            <tr><td colSpan={7}><div className="pg-empty" style={{ margin: '10px 0' }}>Loading links…</div></td></tr>
          ) : embeds.length === 0 ? (
            <tr><td colSpan={7}><div className="pg-empty" style={{ margin: '10px 0' }}>No share links yet — create one from any file’s Share action.</div></td></tr>
          ) : (
            embeds.map((e) => {
              const s = statusOf(e);
              const ab = ACCESS_BADGE[e.access] ?? ["gray", e.access];
              return (
                <tr key={e._id} style={s.dim ? { opacity: .55 } : undefined}>
                  <td className="nm">
                    <span className="mini-ico" style={{ background: '#7A5C2E' }}>LNK</span>{' '}
                    {models[e.modelId]?.name || e.clientName || 'Shared model'}
                    {e.clientName ? <span className="hint" style={{ fontWeight: 400 }}> → {e.clientName}</span> : null}
                  </td>
                  <td><span className={'badge ' + ab[0]}>{ab[1]}</span></td>
                  <td>{e.viewCount}</td>
                  <td>{e.downloadCount || '—'}</td>
                  <td>{fmtExp(e)}</td>
                  <td><span className={'badge ' + s.badge[0]}>{s.badge[1]}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    {s.dim
                      ? <><button className="btn ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => renew(e)}>Renew</button> <button className="btn danger" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => revoke(e)}>Delete</button></>
                      : <><CopyBtn className="btn ghost" label="Copy" value={urlFor(e)} /> <button className="btn ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => openShare(models[e.modelId] ?? null)}>Settings</button> <button className="btn danger" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => revoke(e)}>Revoke</button></>}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </>
  );
}
